/**
 * UI actions that call __ucCmp (accept service, open cookie settings).
 * API reference: https://usercentrics.com/docs/web/features/api/control-functionality/
 */
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import type { UcServiceId } from './usercentricsConfig';
import {
  acceptServiceConsent,
  openConsentSettings as openConsentSettingsApi,
} from './usercentricsApi';
import { useUsercentricsContext } from './UsercentricsContext';

type UsercentricsActions = {
  acceptService: (serviceId: UcServiceId) => Promise<void>;
  openConsentSettings: (serviceId?: UcServiceId) => Promise<void>;
  isInitialized: boolean;
  cmpLoadFailed: boolean;
};

export const useUsercentricsActions = (): UsercentricsActions => {
  const { isCmpEnabled, isInitialized, cmpLoadFailed, refreshConsents } = useUsercentricsContext();

  const acceptService = useCallback(
    async (serviceId: UcServiceId): Promise<void> => {
      if (!isCmpEnabled) return;

      try {
        await acceptServiceConsent(serviceId);
        await refreshConsents();
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [isCmpEnabled, refreshConsents]
  );

  const openConsentSettings = useCallback(
    async (serviceId?: UcServiceId): Promise<void> => {
      if (!isCmpEnabled) return;

      try {
        await openConsentSettingsApi(serviceId);
        await refreshConsents();
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [isCmpEnabled, refreshConsents]
  );

  return {
    acceptService,
    openConsentSettings,
    isInitialized,
    cmpLoadFailed,
  };
};
